import { useSelector } from "react-redux";

import {
  removeStartFromUnderscore,
  stringArrayToNumberArray
} from "@/helpers/string";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import type { RootState } from "@/store";
import type { Dataset } from "@/types/dataset";
import type {
  AnalysisOutput,
  AreaInput,
  Catchment,
  DatasetConfigAttributeResponse,
  DatasetConfigResponse
} from "@/types/project";
import {
  AreaInputType,
  ECatchmentInputBy,
  ECatchmentTransport,
  ECatchmentType,
  EOutputAnalysisType,
  EOutputGridType,
  ERoadAccessMeasurement,
  ScoringOptionsEnum
} from "@/types/spatial";
import { iterateRows } from "@/utils/catchment";
import {
  createFinalAttributes,
  getGridConfig,
  getMaxLevelAdminArea,
  mapDatasetFilters,
  type MappedFilter,
  processCatchmentDatasetAttributes,
  separateAdminAreaAttributes
} from "@/utils/configurationMapper";
import { getNegativeImpactValue, parseWeightValue } from "@/utils/weightValues";

const useConfigurationMappers = () => {
  const { isAdminTreeEnabled } = useAppFeatures();
  const {
    datasets: currentDatasets,
    filters: datasetFilters,
    visualizationOrder: currentVisualizationOrder,
    weightValues,
    history
  } = useSelector((state: RootState) => state.dataset);

  const {
    adminAreaSettings,
    adminTreeSettings,
    catchmentSettings,
    spatialConfig,
    polygonSettings,
    overrideGlobalFilter
  } = useSelector((state: RootState) => state.spatial);
  const { showAnalysisResultPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const {
    adminAreaSettings: globalAdminAreaSettings,
    selectedAreaIds: globalSelectedAreaIds
  } = useSelector((state: RootState) => state.globalFilter);

  /* Check if the analysis result popup is open, will get the datasets from history
    Reference: file useAnalysisResult.tsx line 49
  */
  const datasets = showAnalysisResultPopup ? history.datasets : currentDatasets;
  const visualizationOrder = showAnalysisResultPopup
    ? history.visualizationOrder
    : currentVisualizationOrder;

  const mapCatchmentAreaInput = (): AreaInput | null => {
    if (!catchmentSettings) return null;
    const {
      catchmentType,
      roadAccessConfig,
      radiusData = [],
      placeCoordinates
    } = catchmentSettings;
    const byRadius = catchmentType === ECatchmentType.BY_RADIUS;
    const isDistance =
      !byRadius &&
      roadAccessConfig?.measurementType === ERoadAccessMeasurement.DISTANCE;

    const inputByLocation =
      catchmentSettings.catchmentInputBy === ECatchmentInputBy.BY_LOCATION;
    const inputByDataset =
      catchmentSettings.catchmentInputBy === ECatchmentInputBy.BY_DATASET;
    const hasCoordinates = placeCoordinates && placeCoordinates.length > 0;
    const catchmentByDataset = Object.values(
      catchmentSettings.datasetConfig?.datasets ?? {}
    );
    const isochroneData = roadAccessConfig?.isochroneData;

    let locations: Catchment["locations"] = undefined;

    if (inputByLocation && hasCoordinates) {
      locations = placeCoordinates.map((place, index) => ({
        name: place.locationName || `Marker #${index + 1}`,
        coordinates: {
          latitude: place.lat,
          longitude: place.lng
        },
        catchment_area: isochroneData?.[index]?.data ?? null // send isochrone data instead of null, because on API analysis/:id/config does not return isochrone data
      }));
    } else if (inputByDataset && !byRadius && catchmentByDataset) {
      const locationResults: Catchment["locations"] = [];
      iterateRows(
        Object.values(catchmentByDataset),
        (long, lat, label, index) => {
          locationResults.push({
            name: label ?? "",
            coordinates: {
              latitude: lat,
              longitude: long
            },
            catchment_area: isochroneData?.[index]?.data ?? null // send isochrone data instead of null, because on API analysis/:id/config does not return isochrone data
          });
        }
      );
      locations = locationResults;
    }

    return {
      adm_area_id: undefined,
      catchment: {
        profile:
          (byRadius
            ? ECatchmentType.BY_RADIUS
            : roadAccessConfig?.routingType) ?? ECatchmentTransport.WALKING,
        exclusions: byRadius
          ? ([] as string[])
          : (roadAccessConfig?.roadExclusions as string[]),
        metric: byRadius
          ? "distance"
          : (roadAccessConfig?.measurementType ?? ""),
        contours: byRadius
          ? stringArrayToNumberArray(radiusData)
          : isDistance
            ? stringArrayToNumberArray(roadAccessConfig.distances)
            : stringArrayToNumberArray(roadAccessConfig?.durations),
        locations,
        dataset:
          inputByDataset && catchmentByDataset
            ? {
                id: catchmentByDataset[0]?.rawDataset.dataset_id,
                name: catchmentByDataset[0]?.rawDataset.name,
                display_name: catchmentByDataset[0]?.dataset.data.display_name,
                spatial_aggregation_type:
                  catchmentByDataset[0]?.rawDataset.spatial_aggregation_type,
                attributes: processCatchmentDatasetAttributes(
                  catchmentByDataset[0]?.dataset,
                  catchmentByDataset[0]?.filters
                ),
                color_based:
                  catchmentByDataset[0]?.dataset.selectedCategory ?? undefined,
                label_config: {
                  ...catchmentByDataset[0]?.dataset.style.labelConfig,
                  enable_ranking:
                    catchmentByDataset[0]?.dataset.style.rankConfig
                      ?.isEnabled ?? false,
                  max_ranking:
                    catchmentByDataset[0]?.dataset.style.rankConfig
                      ?.maxRanking ?? 10
                },
                weight: parseWeightValue(
                  weightValues[catchmentByDataset[0]?.rawDataset.dataset_id]
                    ?.weight
                )
              }
            : undefined
      }
    };
  };

  const mapAdminAreaInput = (): AreaInput | null => {
    // When admin tree is enabled, use the first explicitly selected area ID
    if (isAdminTreeEnabled && adminTreeSettings?.selectedAreaIds?.length > 0) {
      return {
        adm_area_ids: adminTreeSettings.selectedAreaIds,
        is_global_admin_area: !overrideGlobalFilter
      };
    }

    // Fallback to dropdown-based selection
    const maxLevel = getMaxLevelAdminArea(adminAreaSettings);
    if (!maxLevel) return null;
    return {
      adm_area_id: adminAreaSettings?.[maxLevel]?.value,
      is_global_admin_area: !overrideGlobalFilter
    };
  };

  const mapCustomCatchmentInput = (): AreaInput | null => {
    if (Object.keys(polygonSettings).length === 0) return null;

    return {
      custom_catchments: {
        type: "FeatureCollection",
        features: Object.values(polygonSettings).map((polygon) => ({
          ...polygon.polygon
        }))
      }
    };
  };

  const mappingAreaInput = (): AreaInput => {
    switch (spatialConfig.areaInput?.value) {
      case AreaInputType.CATCHMENT:
        return mapCatchmentAreaInput() ?? {};
      case AreaInputType.POLYGON:
        return mapCustomCatchmentInput() ?? {};
      default:
        return mapAdminAreaInput() ?? {};
    }
  };

  const mappingAnalysisOutput = (): AnalysisOutput | undefined => {
    if (!spatialConfig.outputType) {
      return {
        scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
      };
    }

    if (spatialConfig.outputType === EOutputAnalysisType.PROFILING)
      return {
        type: EOutputAnalysisType.PROFILING,
        scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
      };

    if (!spatialConfig.gridType) {
      return {
        scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
      };
    }

    const geohashType = spatialConfig.gridType === EOutputGridType.GEOHASH;
    const gridLevel = Number(
      geohashType
        ? (spatialConfig.gridResolution?.value ?? 0)
        : (spatialConfig.h3Resolution?.value ?? 0)
    );
    return {
      type: spatialConfig.gridType ?? "",
      grid_level: gridLevel,
      scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
    };
  };

  // Filter datasets by acceptable spatial types
  const filterDatasetsByType = () => {
    return Object.entries(datasets).filter(([, dataset]) =>
      [
        "poi",
        "polygon",
        "adm_area",
        "line",
        EOutputAnalysisType.H3,
        EOutputAnalysisType.GEOHASH,
        "line"
      ].includes(dataset.properties.spatialType)
    );
  };

  const processDatasetAttributes = (
    datasetId: string
  ): DatasetConfigAttributeResponse[] => {
    const filtersArray = datasetFilters[datasetId] ?? [];
    const weightScore = weightValues[datasetId] ?? {};
    const filters: MappedFilter[] = filtersArray.map((filter) =>
      mapDatasetFilters(filter)
    );
    const currDataset = datasets[datasetId];
    const attributeSource =
      currDataset.properties.spatialType === "H3" &&
      currDataset.data.source === "bvt"
        ? currDataset.data.schema
        : currDataset.categories;

    // Track matched filters to prevent the same filter from being matched to multiple attributes
    // This can happen when one attribute's id equals another's paired_key_attribute_id
    // Note: Admin area filters are excluded from this deduplication because they intentionally
    // match multiple schema items (adm_area_level_1, adm_area_level_2, etc.) and get deduplicated later
    const matchedFilterKeys = new Set<string>();

    return (
      attributeSource?.map((attribute) => {
        const attributeId = attribute.paired_key_attribute_id ?? attribute.id;
        let matchedIsAdmArea = false;

        const matchedFilter = filters.find((f) => {
          const isAdmAreaFilter = f.filter?.criteria?.includes("::");

          if (isAdmAreaFilter) {
            // Admin area filter has ::. For admin area filters, only check attribute id
            if (f.attributeId === attributeId) {
              matchedIsAdmArea = true;
              return true;
            }
          } else {
            // For non-admin-area filters, skip if already matched to prevent duplication
            const filterKey = `${f.attributeId}_${f.attributeCode ?? ""}`;
            if (matchedFilterKeys.has(filterKey)) return false;

            if (
              f.attributeId === attributeId &&
              f.attributeCode === attribute.attribute_code
            ) {
              matchedIsAdmArea = false;
              return true;
            }
          }
          return false;
        }) || { filter: null, filterType: null };

        // Mark non-admin-area filters as matched to prevent duplication
        if (matchedFilter.filter && !matchedIsAdmArea) {
          const filterKey = `${(matchedFilter as MappedFilter).attributeId}_${(matchedFilter as MappedFilter).attributeCode}`;
          matchedFilterKeys.add(filterKey);
        }

        const { filter, filterType } = matchedFilter;

        const attributeEntry = weightScore?.attributes?.[attributeId];

        const negativeImpact = getNegativeImpactValue(attributeEntry);

        return {
          filter,
          id: attributeId,
          weight_score: parseWeightValue(attributeEntry),
          ...(negativeImpact !== undefined
            ? {
                weight_negative_impact: negativeImpact
              }
            : {}),
          ...(filterType ? { filterType } : {}),
          ...(attribute.attribute_code
            ? { attribute_code: attribute.attribute_code }
            : {})
        };
      }) || []
    );
  };

  const mappingDataset = (): DatasetConfigResponse[] => {
    if (!Object.keys(datasets).length) return [];

    const filteredDatasets = filterDatasetsByType();

    // Optimize lookup by creating a Map for O(1) access
    const datasetMap = new Map(filteredDatasets);

    const orderedFilteredDatasets = visualizationOrder
      .filter((datasetId) => datasetMap.has(datasetId))
      .map(
        (datasetId) =>
          [datasetId, datasetMap.get(datasetId)] as [
            string,
            Dataset | undefined
          ]
      )
      .filter((entry): entry is [string, Dataset] => entry[1] !== undefined);

    return orderedFilteredDatasets.map(([datasetId, dataset]) => {
      const attributes = processDatasetAttributes(datasetId);

      // nonAdminAttributes can have non-unique attribute id
      // Thus, the Set cannot be used to filter them
      const { adminAreaAttributes, nonAdminAttributes } =
        separateAdminAreaAttributes(attributes, dataset);

      const finalAttributes = createFinalAttributes(
        nonAdminAttributes,
        adminAreaAttributes
      );

      return {
        id: removeStartFromUnderscore(datasetId),
        display_name: dataset?.data?.display_name,
        spatial_aggregation_type: dataset.properties.spatialType,
        attributes: finalAttributes,

        color_based: dataset?.selectedCategory,
        color_scale: dataset?.style?.colorFamily,
        opacity:
          dataset.properties.spatialType === "line"
            ? dataset?.style?.stroke?.opacity
            : dataset?.style?.fillOpacity,
        grid_config: getGridConfig(dataset),
        color_steps: dataset?.style?.colorSteps,
        stroke_width:
          dataset.properties.spatialType === "line"
            ? dataset?.style?.stroke?.width
            : undefined,
        label_config: {
          ...dataset?.style?.labelConfig,
          enable_ranking: dataset?.style?.rankConfig?.isEnabled ?? false,
          max_ranking: dataset?.style?.rankConfig?.maxRanking ?? 10
        },
        weight: parseWeightValue(weightValues[datasetId]?.weight),
        is_global_admin_area: dataset.is_global_admin_area ?? false,
        ...(dataset.group_by === "none" ? {} : { group_by: dataset.group_by })
      };
    });
  };

  const mappingAdmAreaIds = (): string[] => {
    if (isAdminTreeEnabled) {
      return globalSelectedAreaIds;
    }
    const maxLevel = getMaxLevelAdminArea(globalAdminAreaSettings);
    if (!maxLevel) return [];

    const value = globalAdminAreaSettings?.[maxLevel]?.value;
    return value ? [value] : [];
  };

  return {
    getGridConfig,
    mappingDataset,
    mappingAreaInput,
    mappingAdmAreaIds,
    mappingAnalysisOutput
  };
};

export default useConfigurationMappers;
